import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { format, addDays } from 'date-fns';
import {
  ArrowLeft, ArrowRight, Check, Clock, Loader2, XCircle,
  Calendar as CalendarIcon, CalendarDays, Minus, Plus, Gift, X
} from 'lucide-react';
import { useHolidays } from '../../hooks/useQueries';
import { PromoRewardNotice, MeetingRoomRewardPanel } from './PromoRewardNotice';
import { meetingRoomRewardPreview, offerLive, getPromotion, DAY_PASS_OFFER } from '../../lib/promotions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Mirrors MULTI_DATE_DAY_PASS_MAX_DATES in backend/routes/bookings.py.
const MAX_DAY_PASS_DATES = 14;

const QtyControl = ({ value, onChange, min = 1, max = 20 }) => (
  <div className="flex items-center gap-1.5">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="w-7 h-7 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors" disabled={value <= min}>
      <Minus className="w-3 h-3" />
    </button>
    <Input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || min)))} className="w-14 h-7 text-center text-sm px-1 bg-input/50" data-testid="addon-qty-input" />
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="w-7 h-7 rounded-md border border-border flex items-center justify-center hover:bg-secondary transition-colors" disabled={value >= max}>
      <Plus className="w-3 h-3" />
    </button>
  </div>
);

export const DateTimeSelector = ({
  selectedBookableData, selectedVenueData,
  selectedDate, setSelectedDate,
  selectedDates = [], onSelectDayPassDates = () => {}, multiDateUnavailable = [],
  startTime, setStartTime, endTime, setEndTime,
  loading, availability,
  getFilteredStartTimes, getAvailableEndTimes, isTimeSlotAvailable,
  addOns, setAddOns, calculateTotal,
  passQuantity = 1, setPassQuantity = () => {},
  passDays = 1, setPassDays = () => {},
  onBack, onNext,
  wallet, user,
  dayPassBalance, dayPassApplicable,
  promoReward = null, bulkDiscountOn = true,
}) => {
  const isToday = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const { data: holidays = [] } = useHolidays();
  const holidayDateSet = new Set(holidays.map(h => h.date));
  // Sundays are closed to the public. Admin/Centre Manager/Cluster Manager may still
  // book Sunday, but only as an internal booking (chosen in the next step) — the
  // backend re-validates this, so relaxing the calendar here is just UX, not the gate.
  const canBookSunday = ['admin', 'center_manager', 'cluster_manager'].includes(user?.role);
  // Day Passes: pick one date, or several to buy them together (one payment,
  // one bill). Other spaces book a single date.
  const isDaily = selectedBookableData?.booking_mode === 'daily';
  const isMultiDate = isDaily && selectedDates.length > 1;
  // "Multiple days" is an explicit mode so customers can see it exists;
  // reopens in that mode when coming back with several dates picked.
  const [multiMode, setMultiMode] = useState(selectedDates.length > 1);
  const chooseDateMode = (multi) => {
    setMultiMode(multi);
    if (!multi && selectedDates.length > 1) onSelectDayPassDates(selectedDates.slice(0, 1));
  };

  const isDayDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isSunday = date.getDay() === 0;
    return date < today || (isSunday && !canBookSunday) || holidayDateSet.has(format(date, 'yyyy-MM-dd'));
  };

  // "+ Next N days": N open days from the first date picked (or the first
  // day still bookable), skipping Sundays and holidays.
  const pickNextDays = (n) => {
    let day = selectedDates[0] ? new Date(selectedDates[0]) : new Date();
    day.setHours(0, 0, 0, 0);
    if (!selectedDates[0]) {
      // Same-day passes close an hour before the centre does (backend rule).
      const [ch, cm] = (selectedVenueData?.closing_time || '20:00').split(':').map(Number);
      const now = new Date();
      if (now.getHours() * 60 + now.getMinutes() >= ch * 60 + cm - 60) day = addDays(day, 1);
    }
    const picked = [];
    for (let i = 0; picked.length < n && i < 60; i++, day = addDays(day, 1)) {
      if (!isDayDisabled(day) && day.getDay() !== 0) picked.push(new Date(day));
    }
    onSelectDayPassDates(picked);
  };

  // Sale offer nudge (Buy X, Get X Free counts passes across all dates).
  const offerBlock = offerLive(DAY_PASS_OFFER) ? (getPromotion()?.offers?.[DAY_PASS_OFFER]?.block || 3) : 0;
  const passesPerDate = Math.max(1, parseInt(passQuantity) || 1);
  const totalPasses = passesPerDate * selectedDates.length;
  const datesNeeded = Math.ceil(Math.max(0, offerBlock - totalPasses) / passesPerDate);
  const datesLabel = selectedDates.map(d => format(d, 'd MMM')).join(', ');
  const unavailableLabel = multiDateUnavailable
    .map(d => format(new Date(`${d}T00:00:00`), 'd MMM')).join(', ');
  const dayPassDatesAvailable = !!startTime && !!endTime
    && isTimeSlotAvailable(startTime, endTime) && multiDateUnavailable.length === 0;
  // Deep-cleaning notice: Day Pass bookings on Saturday only.
  const isSaturdayDayPass = isDaily && (isMultiDate ? selectedDates : [selectedDate]).some(d => d && d.getDay() === 6);

  const SaturdayCleaningNotice = () => (
    <div className="mt-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400" data-testid="saturday-cleaning-notice">
      <span className="font-semibold">Heads up:</span> Deep cleaning is carried out at the centre every Saturday, which may cause some disturbance. We apologise for the inconvenience and wanted to let you know in advance.
    </div>
  );

  // Compute credits required for THIS booking — mirrors backend `calculate_booking_credits`.
  // Rate map matches /app/backend/models.py VENUE_CREDIT_RATES.
  const computeCreditsRequired = () => {
    const venueName = selectedVenueData?.name || '';
    const bookingMode = selectedBookableData?.booking_mode || 'hourly';
    const capacity = Math.max(selectedBookableData?.capacity || 1, 1);
    const hours = calculateTotal()?.hours || 0;
    if (!hours) return 0;
    if (bookingMode === 'daily' || bookingMode === 'monthly') {
      const price = selectedBookableData?.hourly_rate || selectedBookableData?.price_per_hour || 0;
      return +(price / 100).toFixed(2);
    }
    const RATES = { Shankaraa: 1.0, Southend: 1.0, 'Mini Forest': 1.5, Miniforest: 1.5, Create: 1.5, JPN: 1.5, Opus: 2.0 };
    let rate = 1.5; // default
    for (const k of Object.keys(RATES)) {
      if (venueName.toLowerCase().includes(k.toLowerCase())) { rate = RATES[k]; break; }
    }
    return +(rate * capacity * hours).toFixed(1);
  };

  const creditsRequired = computeCreditsRequired();
  const availableCredits = (wallet?.balance || 0) + (wallet?.topup_balance || 0);
  const willUseCredits = availableCredits >= creditsRequired && creditsRequired > 0;
  const dayPassRemaining = dayPassBalance?.total_remaining || 0;
  const dayPassRequestedQty = Math.max(1, parseInt(passQuantity) || 1);
  const willUseDayPass = !!dayPassApplicable && dayPassRemaining > 0;
  // Meeting Room 2+2 estimate (exact figures come from the backend at checkout).
  const meetingRoomReward = meetingRoomRewardPreview({
    bookableType: selectedBookableData?.bookable_type,
    hours: calculateTotal()?.hours || 0,
    paidAmount: calculateTotal()?.total,
    paidCredits: creditsRequired,
  });
  const offerEligible = !!user && user.role === 'client' && !user.is_guest;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold mb-2">Select Date & Time</h2>
          <p className="text-muted-foreground">Choose your preferred booking slot</p>
        </div>
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>
      </div>

      {/* Selected Space Summary */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4 flex items-center gap-4">
          <img
            src={(() => {
              const img = selectedBookableData?.images?.[0];
              if (!img) return 'https://images.unsplash.com/photo-1572025442646-866d16c84a54?w=200';
              return img.startsWith('/') ? `${BACKEND_URL}${img}` : img;
            })()}
            alt={selectedBookableData?.name}
            className="w-20 h-20 rounded-lg object-cover"
          />
          <div>
            <h3 className="font-heading font-semibold">{selectedBookableData?.name}</h3>
            <p className="text-sm text-muted-foreground">{selectedVenueData?.name}</p>
            <p className="text-sm font-semibold text-primary mt-1">
              ₹{selectedBookableData?.hourly_rate?.toLocaleString('en-IN')}
              {selectedBookableData?.booking_mode === 'monthly' ? '/month' : selectedBookableData?.booking_mode === 'daily' ? '/day' : '/hr'}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Calendar */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-primary" /> {isDaily && multiMode ? 'Select Dates' : 'Select Date'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isDaily && (
              <div className="mb-4 space-y-3" data-testid="day-pass-date-mode">
                <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-secondary" role="tablist">
                  {[
                    { multi: false, icon: CalendarIcon, label: 'One day' },
                    { multi: true, icon: CalendarDays, label: 'Multiple days' },
                  ].map(({ multi, icon: Icon, label }) => (
                    <button
                      key={label} type="button" role="tab" aria-selected={multiMode === multi}
                      onClick={() => chooseDateMode(multi)}
                      className={`flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-all ${
                        multiMode === multi ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={multi ? 'date-mode-multiple' : 'date-mode-single'}
                    >
                      <Icon className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
                {!multiMode ? (
                  <button
                    type="button" onClick={() => chooseDateMode(true)}
                    className="w-full text-left px-3 py-2 rounded-md border border-dashed border-primary/40 bg-primary/5 text-xs text-primary hover:bg-primary/10 transition-colors"
                    data-testid="multi-date-cta"
                  >
                    <span className="font-semibold">Coming in on more than one day?</span> Book all your dates together — one payment, one bill.
                    {offerBlock > 0 && <> Pick {offerBlock}+ dates and get the same number of Day Passes free.</>}
                  </button>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Tap each day you want on the calendar (up to {MAX_DAY_PASS_DATES}). Tap a day again to remove it.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[3, 5].map(n => (
                        <button
                          key={n} type="button" onClick={() => pickNextDays(n)}
                          className="px-2.5 py-1 rounded-full border border-border text-xs hover:border-primary hover:text-primary transition-colors"
                          data-testid={`quick-pick-${n}`}
                        >
                          + Next {n} days
                        </button>
                      ))}
                      {selectedDates.length > 0 && (
                        <button
                          type="button" onClick={() => onSelectDayPassDates([])}
                          className="px-2.5 py-1 rounded-full text-xs text-muted-foreground hover:text-destructive transition-colors"
                          data-testid="clear-dates"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <Calendar
              {...(isDaily && multiMode
                ? { mode: 'multiple', selected: selectedDates, onSelect: onSelectDayPassDates, max: MAX_DAY_PASS_DATES }
                : { mode: 'single', selected: selectedDate, onSelect: setSelectedDate })}
              disabled={isDayDisabled}
              className="rounded-md border-0"
              data-testid="booking-calendar"
            />
            {selectedDate && holidayDateSet.has(format(selectedDate, 'yyyy-MM-dd')) && (
              <p className="text-xs text-red-500 mt-2 px-2">This date is closed for bookings.</p>
            )}
            {isDaily && multiMode && (
              <div className="mt-3 space-y-2" data-testid="selected-dates">
                {selectedDates.length === 0 ? (
                  <p className="text-xs text-muted-foreground px-1">No dates picked yet.</p>
                ) : (
                  <>
                    <p className="text-xs font-semibold px-1">
                      {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedDates.map(d => (
                        <span key={d.getTime()} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                          {format(d, 'EEE d MMM')}
                          <button
                            type="button" aria-label={`Remove ${format(d, 'd MMM')}`}
                            onClick={() => onSelectDayPassDates(selectedDates.filter(x => x.getTime() !== d.getTime()))}
                            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-primary/20"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {offerBlock > 0 && totalPasses > 0 && (
                  <div
                    className={`flex items-start gap-2 px-3 py-2 rounded-md border text-xs ${
                      totalPasses >= offerBlock
                        ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400'
                        : 'bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300'
                    }`}
                    data-testid="multi-date-offer-progress"
                  >
                    <Gift className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      {totalPasses >= offerBlock
                        ? <><strong>Offer unlocked:</strong> {totalPasses} paid passes earn {totalPasses} free Day Passes.</>
                        : <>Add <strong>{datesNeeded} more date{datesNeeded !== 1 ? 's' : ''}</strong> to get your Day Passes free — buy {offerBlock} or more, get the same number free.</>}
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Time Selection */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Clock className="w-5 h-5 mr-2 text-primary" /> Select Time
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!selectedDate ? (
              <p className="text-muted-foreground text-sm text-center py-8">Please select a date first</p>
            ) : loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : selectedBookableData?.booking_mode === 'daily' ? (
              <>
                {/* Day Pass — fixed time, no selectors */}
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20" data-testid="day-pass-time-info">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Day Pass Timing</p>
                      <p className="text-xs text-muted-foreground">Fixed hours for all day passes</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 p-3 rounded-md bg-background/60">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Check-in</p>
                      <p className="font-heading font-semibold text-lg">9:00 AM</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Check-out</p>
                      <p className="font-heading font-semibold text-lg">8:00 PM</p>
                    </div>
                  </div>
                </div>

                {/* Bulk Day Pass — Quantity & Days */}
                <div className="p-4 rounded-lg border border-border bg-card/60" data-testid="day-pass-bulk-controls">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold">{multiMode ? 'Passes per date' : 'Quantity & Validity'}</p>
                      <p className="text-xs text-muted-foreground">{multiMode ? 'How many people are coming on each date you picked.' : 'Passes for this date. Coming on several days? Switch to Multiple days.'}{bulkDiscountOn ? ' 10% off when total ≥ 5.' : ''}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">{multiMode ? 'Passes per date' : 'Number of passes'}</Label>
                      <Input
                        type="number" min={1} max={multiMode ? 20 : 100} value={passQuantity || 1}
                        onChange={(e) => setPassQuantity(Math.max(1, Math.min(multiMode ? 20 : 100, parseInt(e.target.value) || 1)))}
                        className="mt-1 bg-input/50" data-testid="day-pass-quantity-input"
                      />
                    </div>
                    {multiMode ? (
                      <div>
                        <Label className="text-xs text-muted-foreground">Total passes</Label>
                        <p className="mt-1 h-10 flex items-center font-heading font-semibold" data-testid="day-pass-total-passes">
                          {totalPasses} ({passesPerDate} × {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''})
                        </p>
                      </div>
                    ) : (
                    <div>
                      <Label className="text-xs text-muted-foreground">Valid for (days)</Label>
                      <Input
                        type="number" min={1} max={365} value={passDays || 1}
                        onChange={(e) => setPassDays(Math.max(1, parseInt(e.target.value) || 1))}
                        className="mt-1 bg-input/50" data-testid="day-pass-days-input"
                      />
                    </div>
                    )}
                  </div>
                  {willUseDayPass ? (
                    <div className="mt-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-400" data-testid="day-pass-covers-quantity-notice">
                      Your Day Pass balance covers these {dayPassRequestedQty} pass{dayPassRequestedQty !== 1 ? 'es' : ''} — choose how to pay at checkout.
                    </div>
                  ) : bulkDiscountOn && dayPassRequestedQty * Math.max(1, selectedDates.length) >= 5 && (
                    <div className="mt-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-400" data-testid="bulk-discount-notice">
                      Bulk Day Pass discount (10%) applied — 5 or more passes.
                    </div>
                  )}
                  <PromoRewardNotice reward={promoReward} />
                </div>

                {/* Availability check for day pass */}
                {startTime && endTime && (
                  dayPassDatesAvailable ? (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" data-testid="availability-banner-available">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                          {isMultiDate ? `Available! All ${selectedDates.length} dates are open for booking.` : 'Available! This date is open for booking.'}
                        </p>
                        <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70 mt-0.5">{isMultiDate ? datesLabel : selectedDate && format(selectedDate, 'PPP')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800" data-testid="availability-banner-unavailable">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {isMultiDate
                            ? `Unavailable — ${[!isTimeSlotAvailable(startTime, endTime) && format(selectedDate, 'd MMM'), unavailableLabel].filter(Boolean).join(', ')} already booked.`
                            : 'Unavailable — this date is already booked.'}
                        </p>
                        <p className="text-xs text-red-500/70 dark:text-red-400/70 mt-0.5">{isMultiDate ? 'Untick those dates or choose others.' : 'Please choose a different date.'}</p>
                      </div>
                    </div>
                  )
                )}
                {dayPassDatesAvailable && isSaturdayDayPass && <SaturdayCleaningNotice />}

                {/* Add-ons for day pass */}
                {dayPassDatesAvailable && (
                  <div className="space-y-3" data-testid="addons-section">
                    <Label className="text-sm font-semibold">Additional Services</Label>
                    <div className="space-y-2.5">
                      {/* Stationery */}
                      <div className={`p-3 rounded-lg border transition-colors ${addOns.stationery.enabled ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox checked={addOns.stationery.enabled} onCheckedChange={(v) => setAddOns(p => ({ ...p, stationery: { ...p.stationery, enabled: !!v } }))} data-testid="addon-stationery" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">Stationery (Pen + Notepad)</span>
                            <p className="text-xs text-muted-foreground mt-0.5">Placed at your desk</p>
                          </div>
                          <span className="text-sm font-semibold text-primary whitespace-nowrap">₹50/ea</span>
                        </label>
                        {addOns.stationery.enabled && (
                          <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border/50 pl-8">
                            <span className="text-xs text-muted-foreground">Quantity</span>
                            <QtyControl value={addOns.stationery.qty} onChange={(v) => setAddOns(p => ({ ...p, stationery: { ...p.stationery, qty: v } }))} max={50} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Price Preview for day pass */}
                {startTime && endTime && (
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Day Pass</p>
                        <p className="font-heading font-semibold">9:00 AM - 8:00 PM</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="font-heading font-semibold text-primary text-lg">₹{calculateTotal().total.toFixed(2)}</p>
                      </div>
                    </div>
                    {willUseDayPass && (
                      <div className="mt-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs flex items-start gap-2">
                        <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
                        <div>
                          <p className="font-semibold text-emerald-700 dark:text-emerald-400">Or use your Day Pass balance</p>
                          <p className="text-emerald-600 dark:text-emerald-500 mt-0.5">
                            {dayPassRequestedQty} pass{dayPassRequestedQty !== 1 ? 'es' : ''} from your package ({dayPassRemaining} remaining) — choose how to pay at checkout.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t border-primary/10 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{calculateTotal().baseLabel}</span>
                        <span>₹{calculateTotal().baseAmount.toFixed(2)}</span>
                      </div>
                      {calculateTotal().autoDiscountAmount > 0 && (
                        <div className="flex justify-between text-xs text-green-600 font-medium">
                          <span>{calculateTotal().autoDiscountLabel}</span>
                          <span>-₹{calculateTotal().autoDiscountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {calculateTotal().addOnBreakdown?.map((a, i) => (
                        <div key={i} className="flex justify-between text-xs text-muted-foreground">
                          <span>{a.name} ({a.detail})</span>
                          <span>₹{a.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      {selectedBookableData?.gst_applicable && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>GST ({calculateTotal().gstRate}%)</span>
                          <span>₹{calculateTotal().gstAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {isMultiDate ? datesLabel : selectedDate && format(selectedDate, 'PPP')}
                    </p>
                  </div>
                )}
              </>
            ) : selectedBookableData?.booking_mode === 'monthly' ? (
              <>
                {/* Dedicated Desk — monthly term, no time selection */}
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20" data-testid="monthly-time-info">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Dedicated Desk — Monthly</p>
                      <p className="text-xs text-muted-foreground">Full month access, no time selection needed</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 p-3 rounded-md bg-background/60">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Access from</p>
                      <p className="font-heading font-semibold text-lg">{selectedDate && format(selectedDate, 'PP')}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Valid until</p>
                      <p className="font-heading font-semibold text-lg">{selectedDate && format(addDays(selectedDate, 31), 'PP')}</p>
                    </div>
                  </div>
                </div>

                {/* Availability check for dedicated desk */}
                {startTime && endTime && (
                  availability?.slots?.[0]?.available ? (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" data-testid="availability-banner-available">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Available! This desk is open for the selected month.</p>
                        <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70 mt-0.5">{selectedDate && format(selectedDate, 'PPP')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800" data-testid="availability-banner-unavailable">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-600 dark:text-red-400">Unavailable — this desk is already booked for that period.</p>
                        <p className="text-xs text-red-500/70 dark:text-red-400/70 mt-0.5">Please choose a different start date.</p>
                      </div>
                    </div>
                  )
                )}

                {/* Price Preview for dedicated desk */}
                {startTime && endTime && (
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Membership Period</p>
                        <p className="font-heading font-semibold">1 month (31 days)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Total</p>
                        <p className="font-heading font-semibold text-primary text-lg">₹{calculateTotal().total.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-primary/10 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{calculateTotal().baseLabel}</span>
                        <span>₹{calculateTotal().baseAmount.toFixed(2)}</span>
                      </div>
                      {selectedBookableData?.gst_applicable && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>GST ({calculateTotal().gstRate}%)</span>
                          <span>₹{calculateTotal().gstAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedDate && format(selectedDate, 'PPP')} — {selectedDate && format(addDays(selectedDate, 31), 'PPP')}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Start Time */}
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Select value={startTime} onValueChange={(val) => { setStartTime(val); setEndTime(''); }}>
                    <SelectTrigger data-testid="start-time-select" className="bg-input/50">
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent>
                      {getFilteredStartTimes().map(time => {
                        const slot = availability?.slots?.find(s => s.start === time);
                        const isAvailable = slot?.available;
                        return (
                          <SelectItem key={time} value={time} disabled={!isAvailable} className={!isAvailable ? 'opacity-50' : ''}>
                            {time} {!isAvailable && '(Booked)'}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* End Time */}
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Select value={endTime} onValueChange={setEndTime} disabled={!startTime}>
                    <SelectTrigger data-testid="end-time-select" className="bg-input/50">
                      <SelectValue placeholder={startTime ? "Select end time" : "Select start time first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableEndTimes().map(time => {
                        const isAvailable = isTimeSlotAvailable(startTime, time);
                        return (
                          <SelectItem key={time} value={time} disabled={!isAvailable} className={!isAvailable ? 'opacity-50' : ''}>
                            {time} {!isAvailable && '(Unavailable)'}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Availability Banner */}
                {startTime && endTime && (
                  isTimeSlotAvailable(startTime, endTime) ? (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" data-testid="availability-banner-available">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Available! This slot is open for booking.</p>
                        <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70 mt-0.5">{startTime} — {endTime} on {selectedDate && format(selectedDate, 'PPP')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800" data-testid="availability-banner-unavailable">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                        <XCircle className="w-4 h-4 text-red-500" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-600 dark:text-red-400">Unavailable — this time slot is already booked.</p>
                        <p className="text-xs text-red-500/70 dark:text-red-400/70 mt-0.5">Please choose a different time or date.</p>
                      </div>
                    </div>
                  )
                )}

                {/* Add-ons */}
                {startTime && endTime && isTimeSlotAvailable(startTime, endTime) && (
                  <div className="space-y-3" data-testid="addons-section">
                    <Label className="text-sm font-semibold">Additional Services</Label>
                    <div className="space-y-2.5">
                      {/* Parking */}
                      <div className={`p-3 rounded-lg border transition-colors ${addOns.parking.enabled ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox checked={addOns.parking.enabled} onCheckedChange={(v) => setAddOns(p => ({ ...p, parking: { ...p.parking, enabled: !!v } }))} data-testid="addon-parking" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">4-Wheeler Parking</span>
                            <p className="text-xs text-muted-foreground mt-0.5">Covered parking spot for your vehicle</p>
                          </div>
                          <span className="text-sm font-semibold text-primary whitespace-nowrap">₹50/hr</span>
                        </label>
                        {addOns.parking.enabled && (
                          <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border/50 pl-8">
                            <span className="text-xs text-muted-foreground">Number of spots</span>
                            <QtyControl value={addOns.parking.qty} onChange={(v) => setAddOns(p => ({ ...p, parking: { ...p.parking, qty: v } }))} max={10} />
                          </div>
                        )}
                      </div>

                      {/* Stationery */}
                      <div className={`p-3 rounded-lg border transition-colors ${addOns.stationery.enabled ? 'border-primary/40 bg-primary/5' : 'border-border hover:border-primary/30'}`}>
                        <label className="flex items-start gap-3 cursor-pointer">
                          <Checkbox checked={addOns.stationery.enabled} onCheckedChange={(v) => setAddOns(p => ({ ...p, stationery: { ...p.stationery, enabled: !!v } }))} data-testid="addon-stationery" />
                          <div className="flex-1">
                            <span className="text-sm font-medium">Stationery (Pen + Notepad)</span>
                            <p className="text-xs text-muted-foreground mt-0.5">Placed at each seat in the meeting room</p>
                          </div>
                          <span className="text-sm font-semibold text-primary whitespace-nowrap">₹50/ea</span>
                        </label>
                        {addOns.stationery.enabled && (
                          <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-border/50 pl-8">
                            <span className="text-xs text-muted-foreground">Quantity</span>
                            <QtyControl value={addOns.stationery.qty} onChange={(v) => setAddOns(p => ({ ...p, stationery: { ...p.stationery, qty: v } }))} max={50} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Duration & Price Preview */}
                {startTime && endTime && (
                  <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Duration</p>
                        <p className="font-heading font-semibold">{calculateTotal().hours} hour(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Estimated Total</p>
                        <p className="font-heading font-semibold text-primary text-lg">₹{calculateTotal().total.toFixed(2)}</p>
                      </div>
                    </div>
                    {willUseCredits && (
                      <div className="mt-3 px-3 py-2 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs flex items-start gap-2">
                        <svg className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/></svg>
                        <div>
                          <p className="font-semibold text-emerald-700 dark:text-emerald-400">Or pay with credits</p>
                          <p className="text-emerald-600 dark:text-emerald-500 mt-0.5">
                            About {creditsRequired} credits ({availableCredits.toFixed(1)} available) — choose how to pay at checkout.
                          </p>
                        </div>
                      </div>
                    )}
                    <MeetingRoomRewardPanel reward={meetingRoomReward} eligible={offerEligible} />
                    <div className="mt-2 pt-2 border-t border-primary/10 space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{calculateTotal().baseLabel}</span>
                        <span>₹{calculateTotal().baseAmount.toFixed(2)}</span>
                      </div>
                      {calculateTotal().autoDiscountAmount > 0 && (
                        <div className="flex justify-between text-xs text-green-600 font-medium">
                          <span>{calculateTotal().autoDiscountLabel}</span>
                          <span>-₹{calculateTotal().autoDiscountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      {calculateTotal().addOnBreakdown?.map((a, i) => (
                        <div key={i} className="flex justify-between text-xs text-muted-foreground">
                          <span>{a.name} ({a.detail})</span>
                          <span>₹{a.amount.toFixed(2)}</span>
                        </div>
                      ))}
                      {selectedBookableData?.gst_applicable && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>GST ({calculateTotal().gstRate}%)</span>
                          <span>₹{calculateTotal().gstAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {startTime} - {endTime} on {selectedDate && format(selectedDate, 'PPP')}
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!startTime || !endTime || (isMultiDate && !dayPassDatesAvailable)} className="bg-primary hover:bg-primary/90" data-testid="next-step-2-btn">
          Continue <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
